import {
  ArrowLeft,
  Send,
  X,
} from "lucide-react";
import { AutocompleteSelect } from "../../../../shared/components/AutocompleteSelect";
import {
  planningRequestCategoryOptions,
  unitOptions,
} from "../../procurement-options";
import {
  MikroSuggestedRequestComposer,
  type SuggestedRequestDraft,
} from "../planning/MikroSuggestedRequestComposer";
import type {
  useRequestsWorkspace,
} from "./useRequestsWorkspace";

export function RequestComposer({ model }: { model: ReturnType<typeof useRequestsWorkspace>; }) {
  const {
    composerOpen,
    setComposerOpen,
    user,
    form,
    setForm,
    planningComposerStep,
    setPlanningComposerStep,
    createRequest,
    creatingRequest,
  } = model;
  return (<>
    {composerOpen &&
      user.role === "PLANLAMA" &&
      planningComposerStep === "suggestion" ? (
      <MikroSuggestedRequestComposer
        onCancel={() => setComposerOpen(false)}
        onManualEntry={() => setPlanningComposerStep("form")}
        onApply={(draft: SuggestedRequestDraft) => {
          setForm((current) => ({
            ...current,
            ...draft,
            department:
              user.dataScope === "all" ? "Planlama" : user.department,
          }));
          setPlanningComposerStep("form");
        }}
      />
    ) : composerOpen ? (
      <section
        className="inline-form-card"
        role="dialog"
        aria-modal="true"
        aria-label="Satın alma talebi oluştur"
      >
        <div className="section-heading compact request-composer-header">
          <div>
            <span className="section-kicker">Yeni kayıt</span>
            <h2>Satın alma talebi oluştur</h2>
          </div>
          <div className="inline-form-heading-actions">
            {user.role === "PLANLAMA" ? (
              <button
                className="mikro-back-button"
                type="button"
                disabled={creatingRequest}
                onClick={() => setPlanningComposerStep("suggestion")}
              >
                <ArrowLeft size={15} />
                Mikro önerileri
              </button>
            ) : null}
            <button
              className="icon-button"
              aria-label="Formu kapat"
              disabled={creatingRequest}
              onClick={() => setComposerOpen(false)}
            >
              <X size={17} />
            </button>
          </div>
        </div>
        <div className="inline-form-grid">
          <label>
            Ürün veya hizmet
            <input
              disabled={creatingRequest}
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="Örn. Ambalaj kolisi"
            />
          </label>
          <AutocompleteSelect
            label="Kategori"
            disabled={creatingRequest}
            value={form.category}
            options={planningRequestCategoryOptions}
            placeholder="Hammadde veya Ambalaj seçin"
            onChange={(category) =>
              setForm((current) => ({ ...current, category }))
            }
          />
          <label className="form-field-wide">
            İhtiyacın açıklaması{user.role === "SATIN_ALMA" ? " (zorunlu)" : ""}
            <textarea
              disabled={creatingRequest}
              required={user.role === "SATIN_ALMA"}
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              placeholder="Teknik özellikleri ve kullanım amacını yazın"
              rows={3}
            />
          </label>
          <label>
            Miktar
            <input
              type="number"
              disabled={creatingRequest}
              min="0.001"
              step="0.001"
              value={form.quantity}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  quantity: event.target.value,
                }))
              }
            />
          </label>
          <AutocompleteSelect
            label="Birim"
            disabled={creatingRequest}
            value={form.unit}
            options={unitOptions}
            allowCustom
            placeholder="Birim ara veya yaz"
            onChange={(unit) => setForm((current) => ({ ...current, unit }))}
          />
          <label>
            Departman
            <select
              value={form.department}
              disabled={creatingRequest || user.role === "SATIN_ALMA" || user.dataScope !== "all"}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  department: event.target.value,
                }))
              }
            >
              {user.role === "SATIN_ALMA" || user.dataScope !== "all" ? (
                <option>{user.department}</option>
              ) : (
                <>
                  <option>Planlama</option>
                  <option>Üretim</option>
                  <option>Lojistik</option>
                  <option>Teknik</option>
                </>
              )}
            </select>
          </label>
          <label>
            İhtiyaç tarihi
            <input
              type="date"
              disabled={creatingRequest}
              value={form.due}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  due: event.target.value,
                }))
              }
            />
          </label>
          <label>
            Öncelik
            <select
              disabled={creatingRequest}
              value={form.priority}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  priority: event.target.value,
                }))
              }
            >
              <option value="NORMAL">Normal</option>
              <option value="HIGH">Yüksek</option>
              <option value="CRITICAL">Kritik</option>
            </select>
          </label>
        </div>
        <div className="inline-form-actions">
          <button
            className="secondary-button"
            disabled={creatingRequest}
            onClick={() => setComposerOpen(false)}
          >
            Vazgeç
          </button>
          <button className="approve-button" disabled={creatingRequest} onClick={createRequest}>
            <Send size={15} /> {creatingRequest ? "Oluşturuluyor" : "Talebi oluştur"}
          </button>
        </div>
      </section>
    ) : null}
  </>);
}
